import NumberInput from "../../../components/NumberInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PaymentQrisPanel from "../../../components/Modals/PaymentQrisPanel";
import ItemAvatar from "../../../components/ItemAvatar";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import { isAutomaticPaymentMethod, normalizeCurrencyInput } from "../../PosPage/paymentCalculations";
import "./style.css";

const PAYMENT_METHODS = [
  { value: "cash", label: "Tunai", description: "Pembayaran uang tunai" },
  { value: "qris", label: "QRIS", description: "QR dinamis sesuai tagihan" },
  { value: "transfer", label: "Transfer", description: "Transfer ke rekening toko" },
];

const BILL_STATUS = {
  unpaid: { label: "Belum dibayar", className: "bg-warning-lt" },
  partial: { label: "Dibayar sebagian", className: "bg-warning-lt" },
  paid: { label: "Lunas", className: "bg-success-lt" },
  cancelled: { label: "Dibatalkan", className: "bg-danger-lt" },
};

const createPaymentKey = (billId) => {
  if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
    return `bill-${billId}-${window.crypto.randomUUID()}`;
  }
  return `bill-${billId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const productLabel = (item) =>
  [item?.product_name, item?.variant_label].filter(Boolean).join(" · ") || "Produk";

const buildSplitAllocations = (items, groupCount) => {
  const allocations = {};
  let groupCursor = 0;

  (items || []).forEach((item) => {
    const quantities = Array(groupCount).fill(0);
    const totalQty = Math.max(0, Number(item?.qty || 0));
    for (let unit = 0; unit < totalQty; unit += 1) {
      quantities[groupCursor % groupCount] += 1;
      groupCursor += 1;
    }
    allocations[item.sale_detail_id] = quantities;
  });

  return allocations;
};

const allocateAmount = (amount, weights) => {
  const safeAmount = Math.max(0, Number(amount || 0));
  const safeWeights = weights.map((weight) => Math.max(0, Number(weight || 0)));
  const totalWeight = safeWeights.reduce((sum, weight) => sum + weight, 0);
  const allocations = safeWeights.map(() => 0);
  if (safeAmount <= 0 || totalWeight <= 0) return allocations;

  let remaining = safeAmount;
  const lastPositiveIndex = safeWeights.reduce((last, weight, index) => (weight > 0 ? index : last), -1);
  safeWeights.forEach((weight, index) => {
    if (weight <= 0) return;
    const allocation = index === lastPositiveIndex
      ? remaining
      : Math.min(remaining, Math.floor((safeAmount * weight) / totalWeight));
    allocations[index] = Math.min(weight, allocation);
    remaining -= allocations[index];
  });

  return allocations;
};

const BillManagementModal = ({
  sale,
  storeSetting,
  onLoadBills,
  onLoadEligible,
  onSplit,
  onMerge,
  onPay,
  onUndo,
  onChanged,
  onPrintBill,
  readOnly = false,
  canPay = false,
}) => {
  const modalRef = useRef(null);
  const [view, setView] = useState("overview");
  const [bills, setBills] = useState([]);
  const [eligibleBills, setEligibleBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedMergeIds, setSelectedMergeIds] = useState([]);
  const [mergeLabel, setMergeLabel] = useState("");
  const [splitGroupCount, setSplitGroupCount] = useState(2);
  const [splitLabels, setSplitLabels] = useState(["Tagihan 1", "Tagihan 2"]);
  const [splitAllocations, setSplitAllocations] = useState({});
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentKey, setPaymentKey] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [undoReason, setUndoReason] = useState("");

  const loadBills = useCallback(async () => {
    if (!sale?.id) return [];
    setLoading(true);
    setLoadError("");
    const result = await onLoadBills?.(sale.id);
    const nextBills = result?.success ? result.data || [] : [];
    setBills(nextBills);
    if (!result?.success) setLoadError("Tagihan belum dapat dimuat. Silakan coba lagi.");
    setLoading(false);
    return nextBills;
  }, [onLoadBills, sale?.id]);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return undefined;
    const handleShow = () => {
      setView("overview");
      setSelectedBill(null);
      setUndoReason("");
    };
    const handleShown = () => loadBills();
    const handleHidden = () => {
      setBills([]);
      setEligibleBills([]);
      setSelectedMergeIds([]);
      setLoadError("");
    };
    modal.addEventListener("show.bs.modal", handleShow);
    modal.addEventListener("shown.bs.modal", handleShown);
    modal.addEventListener("hidden.bs.modal", handleHidden);
    return () => {
      modal.removeEventListener("show.bs.modal", handleShow);
      modal.removeEventListener("shown.bs.modal", handleShown);
      modal.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [loadBills]);

  useEffect(() => {
    if (sale?.id && modalRef.current?.classList.contains("show")) {
      loadBills();
    }
  }, [loadBills, sale?.id]);

  const openSplit = (bill) => {
    setSplitGroupCount(2);
    setSplitAllocations(buildSplitAllocations(bill?.items, 2));
    setSplitLabels(["Tagihan 1", "Tagihan 2"]);
    setSelectedBill(bill);
    setValidationAttempted(false);
    setView("split");
  };

  const changeSplitGroupCount = (count) => {
    setSplitGroupCount(count);
    setSplitLabels((current) => Array.from({ length: count }, (_, index) => current[index] || `Tagihan ${index + 1}`));
    setSplitAllocations(buildSplitAllocations(selectedBill?.items, count));
    setValidationAttempted(false);
  };

  const updateSplitAllocation = (item, groupIndex, value) => {
    const detailId = item.sale_detail_id;
    const totalQty = Math.max(0, Number(item?.qty || 0));
    setSplitAllocations((current) => {
      const quantities = [...(current[detailId] || Array(splitGroupCount).fill(0))];
      const allocatedElsewhere = quantities
        .slice(0, splitGroupCount - 1)
        .reduce((sum, qty, index) => sum + (index === groupIndex ? 0 : Number(qty || 0)), 0);
      quantities[groupIndex] = Math.min(
        totalQty - allocatedElsewhere,
        Math.max(0, Number(value || 0)),
      );
      quantities[splitGroupCount - 1] = Math.max(
        0,
        totalQty - quantities.slice(0, splitGroupCount - 1).reduce((sum, qty) => sum + Number(qty || 0), 0),
      );

      return { ...current, [detailId]: quantities };
    });
  };

  const openMerge = async (bill) => {
    setSelectedBill(bill);
    setSelectedMergeIds([]);
    setMergeLabel("");
    setView("merge");
    setLoading(true);
    const result = await onLoadEligible?.();
    setEligibleBills(result?.success ? (result.data || []).filter((item) => item.id !== bill.id) : []);
    if (!result?.success) setLoadError("Daftar tagihan yang dapat digabung belum dapat dimuat.");
    setLoading(false);
  };

  const openPayment = (bill) => {
    if (!canPay || bill.can_process === false) return;
    setSelectedBill(bill);
    setPaymentAmount(Number(bill?.due || 0) || "");
    setPaymentMethod("cash");
    setPaymentNote("");
    setPaymentKey(createPaymentKey(bill?.id));
    setValidationAttempted(false);
    setView("payment");
  };

  const splitTotals = useMemo(() => {
    const totals = Array.from({ length: splitGroupCount }, () => ({ qty: 0, total: 0, paid: 0, due: 0 }));
    (selectedBill?.items || []).forEach((item) => {
      const totalQty = Math.max(0, Number(item?.qty || 0));
      const itemTotal = Math.max(0, Number(item?.total || 0));
      const itemPaid = Math.min(itemTotal, Math.max(0, Number(item?.paid || 0)));
      const quantities = splitAllocations[item.sale_detail_id] || Array(splitGroupCount).fill(0);
      let allocatedTotal = 0;
      const lastAllocatedGroup = quantities.reduce((last, qty, index) => Number(qty || 0) > 0 ? index : last, -1);
      const groupTotals = quantities.map((qtyValue, groupIndex) => {
        const qty = Number(qtyValue || 0);
        const groupTotal = qty <= 0
          ? 0
          : groupIndex === lastAllocatedGroup
            ? itemTotal - allocatedTotal
            : Math.floor((itemTotal * qty) / Math.max(1, totalQty));
        allocatedTotal += groupTotal;
        return groupTotal;
      });
      const groupPaidAllocations = allocateAmount(itemPaid, groupTotals);

      quantities.forEach((qtyValue, groupIndex) => {
        const qty = Number(qtyValue || 0);
        const groupTotal = groupTotals[groupIndex] || 0;
        const groupPaid = groupPaidAllocations[groupIndex] || 0;
        totals[groupIndex].qty += qty;
        totals[groupIndex].total += groupTotal;
        totals[groupIndex].paid += groupPaid;
      });
    });

    totals.forEach((group) => {
      group.due = Math.max(0, group.total - group.paid);
    });

    return totals;
  }, [selectedBill?.items, splitAllocations, splitGroupCount]);

  const isSplitValid = splitTotals.length >= 2 && splitTotals.every((group) => group.qty > 0);

  const submitSplit = async () => {
    setValidationAttempted(true);
    if (!selectedBill?.id || !isSplitValid || loading) return;
    const groups = Array.from({ length: splitGroupCount }, (_, groupIndex) => ({
      label: splitLabels[groupIndex]?.trim() || `Tagihan ${groupIndex + 1}`,
      items: (selectedBill.items || [])
        .map((item) => {
          return {
            sale_detail_id: item.sale_detail_id,
            qty: Number(splitAllocations[item.sale_detail_id]?.[groupIndex] || 0),
          };
        })
        .filter((item) => item.qty > 0),
    }));

    setLoading(true);
    const result = await onSplit?.(selectedBill.id, groups);
    if (result?.success) {
      await loadBills();
      await onChanged?.();
      setView("overview");
    }
    setLoading(false);
  };

  const submitMerge = async () => {
    setValidationAttempted(true);
    if (!selectedBill?.id || selectedMergeIds.length === 0 || loading) return;
    setLoading(true);
    const result = await onMerge?.([selectedBill.id, ...selectedMergeIds], mergeLabel);
    if (result?.success) {
      await loadBills();
      await onChanged?.();
      setView("overview");
    }
    setLoading(false);
  };

  const amount = Number(paymentAmount || 0);
  const due = Number(selectedBill?.due || 0);
  const appliedPayment = Math.min(amount, due);

  const submitPayment = async () => {
    setValidationAttempted(true);
    if (!canPay || selectedBill?.can_process === false || !selectedBill?.id || amount <= 0 || !paymentMethod || loading) return;
    setLoading(true);
    const result = await onPay?.(selectedBill.id, {
      payment_amount: amount,
      payment_method: paymentMethod,
      idempotency_key: paymentKey,
      note: paymentNote,
    });
    if (result?.success) {
      setPaymentKey(createPaymentKey(selectedBill.id));
      await loadBills();
      await onChanged?.();
      setView("overview");
    }
    setLoading(false);
  };

  const openUndo = (bill) => {
    setSelectedBill(bill);
    setUndoReason("");
    setValidationAttempted(false);
    setView("undo");
  };

  const submitUndo = async () => {
    setValidationAttempted(true);
    if (!selectedBill?.id || loading || (selectedBill?.undo_requires_reversal && !undoReason.trim())) return;
    setLoading(true);
    const result = await onUndo?.(selectedBill.id, undoReason.trim());
    if (result?.success) {
      await loadBills();
      await onChanged?.();
      setView("overview");
    }
    setLoading(false);
  };

  const renderOverview = () => (
    <>
      <div className="sale-bill-summary">
        <div>
          <span>Total pesanan</span>
          <strong>{formatIDR(sale?.total || 0)}</strong>
          <small>{sale?.invoice_no || `Penjualan #${sale?.id || "-"}`} · {sale?.customer_name || "Umum"}</small>
        </div>
        <div className="sale-bill-summary-metrics">
          <div><span>Tagihan aktif</span><strong>{bills.length}</strong></div>
          <div><span>Sisa pembayaran</span><strong>{formatIDR(bills.reduce((sum, bill) => sum + Number(bill?.due || 0), 0))}</strong></div>
        </div>
      </div>

      {loadError ? <div className="alert alert-warning">{loadError}</div> : null}
      {loading && bills.length === 0 ? (
        <div className="sale-bill-loading"><span className="spinner-border spinner-border-sm text-primary" /> Memuat tagihan...</div>
      ) : (
        <div className="sale-bill-list">
          {bills.map((bill, index) => {
            const status = BILL_STATUS[bill.payment_status] || BILL_STATUS.unpaid;
            return (
              <article className="sale-bill-card" key={bill.id}>
                <div className="sale-bill-card-header">
                  <div className="sale-bill-number"><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{bill.label || bill.bill_no}</strong><small>{bill.bill_no}</small></div></div>
                  <span className={`badge ${status.className}`}>{status.label}</span>
                </div>
                <div className="sale-bill-items">
                  {(bill.items || []).map((item) => (
                    <div className="sale-bill-item" key={item.id}>
                      <ItemAvatar name={productLabel(item)} image={item.image} size="sm" />
                      <div><strong>{productLabel(item)}</strong><small>{item.qty} {item.unit || "pcs"}{item.note ? ` · ${item.note}` : ""}</small></div>
                      <span>{formatIDR(item.total)}</span>
                    </div>
                  ))}
                </div>
                <div className="sale-bill-card-total">
                  <div><span>Total</span><strong>{formatIDR(bill.total)}</strong></div>
                  <div><span>Sisa</span><strong className={Number(bill.due) > 0 ? "text-danger" : "text-success"}>{formatIDR(bill.due)}</strong></div>
                </div>
                <div className="sale-bill-card-actions">
                  <button type="button" className="btn btn-sm btn-ghost-secondary" onClick={() => onPrintBill?.(bill.id)}>Cetak</button>
                  {!readOnly && bill.can_split ? <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openSplit(bill)}>Bagi tagihan</button> : null}
                  {!readOnly && bill.can_merge ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openMerge(bill)}>Gabungkan</button> : null}
                  {!readOnly && bill.can_undo && (!bill.undo_requires_reversal || canPay) ? (
                    <button type="button" className="btn btn-sm btn-ghost-secondary" onClick={() => openUndo(bill)} disabled={loading}>
                      {bill.undo_requires_reversal ? "Koreksi merge" : "Kembalikan"}
                    </button>
                  ) : null}
                  {canPay && bill.can_process !== false && bill.payment_status !== "paid" ? <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => openPayment(bill)}>Bayar tagihan</button> : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </>
  );

  const renderSplit = () => (
    <div className="sale-bill-workspace">
      <div className="sale-bill-workspace-note"><strong>Bagi berdasarkan item</strong><span>Tentukan jumlah untuk setiap tagihan. Kolom terakhir menghitung sisa secara otomatis.</span></div>
      <div className="sale-bill-split-count">
        <div><strong>Jumlah tagihan</strong><span>Default 2, maksimal 8 sesuai jumlah item.</span></div>
        <div className="sale-bill-count-options" role="group" aria-label="Jumlah tagihan hasil split">
          {Array.from(
            { length: Math.max(1, Math.min(8, (selectedBill?.items || []).reduce((sum, item) => sum + Number(item?.qty || 0), 0)) - 1) },
            (_, index) => index + 2,
          ).map((count) => (
            <button key={count} type="button" className={`btn btn-sm ${splitGroupCount === count ? "btn-primary" : "btn-outline-secondary"}`} aria-pressed={splitGroupCount === count} onClick={() => changeSplitGroupCount(count)}>
              {count}
            </button>
          ))}
        </div>
      </div>
      <div className="sale-bill-label-grid">
        {Array.from({ length: splitGroupCount }, (_, index) => (
          <div key={index}>
            <label className="form-label">Nama tagihan {index + 1}</label>
            <input className="form-control" maxLength="100" value={splitLabels[index]} onChange={(event) => setSplitLabels((current) => current.map((label, labelIndex) => labelIndex === index ? event.target.value : label))} />
          </div>
        ))}
      </div>
      <div className="sale-bill-table-scroll">
        <table className="table table-vcenter sale-bill-split-table">
          <thead><tr><th>Produk</th><th>Total qty</th>{Array.from({ length: splitGroupCount }, (_, index) => <th key={index}>{splitLabels[index] || `Tagihan ${index + 1}`}{index === splitGroupCount - 1 ? <small className="d-block text-secondary fw-normal">Otomatis</small> : null}</th>)}</tr></thead>
          <tbody>
            {(selectedBill?.items || []).map((item) => {
              const totalQty = Number(item.qty || 0);
              const quantities = splitAllocations[item.sale_detail_id] || Array(splitGroupCount).fill(0);
              return <tr key={item.id}>
                <td><div className="d-flex align-items-center gap-2"><ItemAvatar name={productLabel(item)} image={item.image} size="sm" /><div><strong className="d-block">{productLabel(item)}</strong><small className="text-secondary">{formatIDR(item.unit_price)} / {item.unit || "pcs"}</small></div></div></td>
                <td><span className="badge bg-secondary-lt">{totalQty} {item.unit || "pcs"}</span></td>
                {Array.from({ length: splitGroupCount }, (_, groupIndex) => (
                  <td key={groupIndex}>
                    {groupIndex === splitGroupCount - 1
                      ? <span className="sale-bill-auto-qty" aria-label={`${splitLabels[groupIndex] || `Tagihan ${groupIndex + 1}`} ${productLabel(item)}`}>{quantities[groupIndex] || 0}</span>
                      : <NumberInput type="number" min="0" max={totalQty} className="form-control sale-bill-qty-input" aria-label={`${splitLabels[groupIndex] || `Tagihan ${groupIndex + 1}`} ${productLabel(item)}`} value={quantities[groupIndex] || 0} onWheel={(event) => event.currentTarget.blur()} onChange={(event) => updateSplitAllocation(item, groupIndex, event.target.value)} />}
                  </td>
                ))}
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      <div className="sale-bill-result-grid">
        {splitTotals.map((group, index) => (
          <div className={`sale-bill-result ${group.qty <= 0 ? "is-empty" : ""}`} key={index}><span>{splitLabels[index] || `Tagihan ${index + 1}`}</span><strong>{formatIDR(group.due)}</strong><small>Sisa dari {formatIDR(group.total)} · {group.qty} item</small></div>
        ))}
      </div>
      {validationAttempted && !isSplitValid ? <div className="invalid-feedback d-block mt-3">Setiap tagihan harus memiliki minimal satu item.</div> : null}
    </div>
  );

  const renderMerge = () => (
    <div className="sale-bill-workspace">
      <div className="sale-bill-workspace-note"><strong>Gabungkan tagihan terbuka</strong><span>Tagihan draft maupun belum lunas dapat dipilih. Pembayaran sebelumnya tetap tercatat.</span></div>
      <div className="mb-3"><label className="form-label">Nama tagihan gabungan</label><input className="form-control" maxLength="100" value={mergeLabel} onChange={(event) => setMergeLabel(event.target.value)} placeholder="Contoh: Meja A1 atau Rombongan Budi" /></div>
      <div className="sale-bill-merge-current"><span>Tagihan utama</span><strong>{selectedBill?.label || selectedBill?.bill_no}</strong><small>Sisa {formatIDR(selectedBill?.due || 0)} dari {formatIDR(selectedBill?.total || 0)}</small></div>
      <div className="sale-bill-merge-list">
        {loading ? <div className="sale-bill-loading"><span className="spinner-border spinner-border-sm text-primary" /> Memuat tagihan lain...</div> : eligibleBills.length === 0 ? <div className="sale-bill-empty"><strong>Belum ada tagihan yang dapat digabung</strong><span>Tagihan draft atau belum lunas lainnya akan tampil di sini.</span></div> : eligibleBills.map((bill) => {
          const checked = selectedMergeIds.includes(bill.id);
          return <label className={`sale-bill-merge-option ${checked ? "is-selected" : ""}`} key={bill.id}>
            <input type="checkbox" className="form-check-input" checked={checked} onChange={() => setSelectedMergeIds((current) => current.includes(bill.id) ? current.filter((id) => id !== bill.id) : [...current, bill.id])} />
            <div><strong>{bill.label || bill.bill_no}</strong><span>{bill.invoice_nos?.join(", ") || "Draft"} · {bill.customer_names?.join(", ") || "Umum"}</span><small>{(bill.items || []).map(productLabel).join(", ")}</small></div>
            <strong>{formatIDR(bill.due)}</strong>
          </label>;
        })}
      </div>
      {validationAttempted && selectedMergeIds.length === 0 ? <div className="invalid-feedback d-block mt-3">Pilih minimal satu tagihan lain untuk digabungkan.</div> : null}
    </div>
  );

  const renderUndo = () => (
    <div className="sale-bill-workspace">
      <div className="sale-bill-correction-alert">
        <span className="sale-bill-correction-icon"><i className="ti ti-arrows-exchange" /></span>
        <div>
          <strong>{selectedBill?.undo_requires_reversal ? "Koreksi penggabungan yang sudah dibayar" : "Kembalikan penggabungan tagihan"}</strong>
          <p>{selectedBill?.undo_requires_reversal
            ? "Pembayaran pada tagihan gabungan akan direversal, stok transaksi draft dikembalikan, lalu setiap tagihan sumber dibuka kembali."
            : "Tagihan gabungan akan ditutup dan seluruh tagihan sumber dibuka kembali seperti sebelum digabung."}</p>
        </div>
      </div>
      <div className="sale-bill-payment-summary sale-bill-correction-summary">
        <span>{selectedBill?.label || selectedBill?.bill_no}</span>
        <strong>{formatIDR(selectedBill?.pay || 0)}</strong>
        <small>Pembayaran yang akan direversal</small>
        <div><p><span>Nilai tagihan</span><strong>{formatIDR(selectedBill?.total || 0)}</strong></p><p><span>Invoice sumber</span><strong>{selectedBill?.invoice_nos?.length || selectedBill?.sale_ids?.length || 0}</strong></p></div>
      </div>
      <label className="form-label mt-3">Alasan koreksi {selectedBill?.undo_requires_reversal ? <span className="akas-required-mark">*</span> : null}</label>
      <textarea
        className={`form-control ${validationAttempted && selectedBill?.undo_requires_reversal && !undoReason.trim() ? "is-invalid" : ""}`}
        rows="3"
        maxLength="500"
        value={undoReason}
        onChange={(event) => setUndoReason(event.target.value)}
        placeholder="Contoh: kasir salah menggabungkan tagihan pelanggan"
      />
      {validationAttempted && selectedBill?.undo_requires_reversal && !undoReason.trim() ? <div className="invalid-feedback d-block">Alasan koreksi wajib diisi.</div> : null}
      <div className="form-hint mt-2">Koreksi tersimpan pada histori audit. Pembayaran dan mutasi stok lama tidak dihapus.</div>
    </div>
  );

  const renderPayment = () => (
    <div className="sale-bill-workspace">
      <div className="row g-4">
        <div className="col-lg-5"><div className="sale-bill-payment-summary"><span>{selectedBill?.label || selectedBill?.bill_no}</span><strong>{formatIDR(due)}</strong><small>Sisa yang perlu dibayar</small><div>{(selectedBill?.items || []).map((item) => <p key={item.id}><span>{item.qty}× {productLabel(item)}</span><strong>{formatIDR(item.total)}</strong></p>)}</div></div></div>
        <div className="col-lg-7">
          <label className="form-label">Metode pembayaran <span className="akas-required-mark">*</span></label>
          <div className="form-selectgroup form-selectgroup-boxes d-flex gap-2 sale-bill-payment-methods mb-3">
            {PAYMENT_METHODS.map((method) => <label className="form-selectgroup-item flex-fill" key={method.value}><input type="radio" className="form-selectgroup-input" name={`bill-payment-${selectedBill?.id}`} checked={paymentMethod === method.value} onChange={() => { setPaymentMethod(method.value); if (isAutomaticPaymentMethod(method.value)) setPaymentAmount(due); }} /><span className="form-selectgroup-label p-3 h-100"><strong className="d-block">{method.label}</strong><small className="text-secondary">{method.description}</small></span></label>)}
          </div>
          <label className="form-label">Jumlah bayar <span className="akas-required-mark">*</span></label>
          <div className="input-group"><span className="input-group-text bg-primary text-white">Rp</span><NumberInput type="number" min="1" className={`form-control form-control-lg ${validationAttempted && amount <= 0 ? "is-invalid" : ""}`} value={paymentAmount} onWheel={(event) => event.currentTarget.blur()} onChange={(event) => setPaymentAmount(normalizeCurrencyInput(event.target.value))} placeholder="0" /></div>
          {validationAttempted && amount <= 0 ? <div className="invalid-feedback d-block">Nominal pembayaran minimal Rp1.</div> : null}
          {!isAutomaticPaymentMethod(paymentMethod) ? <div className="d-flex flex-wrap gap-2 mt-2"><button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setPaymentAmount(due)}>Uang pas</button>{[50000, 100000, 150000].map((quick) => <button type="button" className="btn btn-sm btn-outline-secondary" key={quick} onClick={() => setPaymentAmount(quick)}>{quick / 1000}rb</button>)}</div> : <div className="form-hint mt-1">Terisi otomatis dari sisa tagihan dan tetap dapat disesuaikan.</div>}
          {paymentMethod === "qris" ? <div className="mt-3"><PaymentQrisPanel amount={appliedPayment} storeSetting={storeSetting} /></div> : null}
          <div className="row g-2 my-3"><div className="col-6"><div className="sale-bill-payment-metric"><span>Sisa setelah bayar</span><strong>{formatIDR(Math.max(0, due - appliedPayment))}</strong></div></div><div className="col-6"><div className="sale-bill-payment-metric"><span>Kembalian</span><strong>{formatIDR(Math.max(0, amount - due))}</strong></div></div></div>
          <label className="form-label">Catatan</label><textarea className="form-control" rows="2" maxLength="500" value={paymentNote} onChange={(event) => setPaymentNote(event.target.value)} placeholder="Opsional, contoh: dibayar oleh tamu kedua" />
        </div>
      </div>
    </div>
  );

  const titles = { overview: "Kelola Tagihan", split: "Bagi Tagihan", merge: "Gabungkan Tagihan", payment: "Pembayaran Tagihan", undo: "Koreksi Tagihan Gabungan" };

  return (
    <div ref={modalRef} className="modal modal-blur fade sale-bill-modal" id="modal-sale-bills" tabIndex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-xl" role="document"><div className="modal-content">
        <div className="modal-header">
          <div className="d-flex align-items-center gap-3"><span className="sale-bill-modal-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 3h12v18l-3-2l-3 2l-3-2l-3 2z"/><path d="M9 8h6M9 12h6M9 16h3"/></svg></span><div><div className="text-uppercase text-primary small fw-bold">Split &amp; merge bill</div><h5 className="modal-title">{titles[view]}</h5></div></div>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Tutup" />
        </div>
        <div className="modal-body">{view === "overview" ? renderOverview() : view === "split" ? renderSplit() : view === "merge" ? renderMerge() : view === "undo" ? renderUndo() : renderPayment()}</div>
        <div className="modal-footer">
          {view === "overview" ? <><div className="me-auto text-secondary small">Stok hanya diproses sekali saat tagihan mulai dibayar.</div><button type="button" className="btn btn-ghost-secondary" data-bs-dismiss="modal">Tutup</button></> : <><button type="button" className="btn btn-link link-secondary me-auto" onClick={() => { setView("overview"); setValidationAttempted(false); }}>Kembali</button>{view === "split" ? <button type="button" className="btn btn-primary" disabled={loading} onClick={submitSplit}>{loading ? "Menyimpan..." : "Simpan Pembagian"}</button> : view === "merge" ? <button type="button" className="btn btn-primary" disabled={loading || eligibleBills.length === 0} onClick={submitMerge}>{loading ? "Menggabungkan..." : `Gabungkan ${selectedMergeIds.length + 1} Tagihan`}</button> : view === "undo" ? <button type="button" className="btn btn-danger" disabled={loading || (selectedBill?.undo_requires_reversal && !undoReason.trim())} onClick={submitUndo}>{loading ? "Mengoreksi..." : "Ya, Koreksi Penggabungan"}</button> : <button type="button" className="btn btn-primary" disabled={loading || !canPay || selectedBill?.can_process === false} onClick={submitPayment}>{loading ? "Memproses..." : "Simpan Pembayaran"}</button>}</>}
        </div>
      </div></div>
    </div>
  );
};

export default BillManagementModal;
